import { ReactNode, useContext } from "react";
import { Box, Container, Flex, Price, Text, LegacyText } from "flicket-ui";
import flatten from "lodash/flatten";
import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import uniqBy from "lodash/uniqBy";
import { useRouter } from "next/router";
import {
  BottomBar,
  Card,
  ContentContainer,
  Footer,
  Menu,
  Seo,
  Status,
  SubHeader,
} from "~components";
import { Layout } from "~components/common/Layout";
import { NonLoggedInRenewalBanner } from "~components/memberships/nonLoggedInRenewalBanner";
import { RenewalBanner } from "~components/memberships/renewalBanner";
import { UserContext } from "~context";
import { usePointsService } from "~features/points/hooks/usePointsService";
import { LandingPage, MembershipKind, MembershipsQuery } from "~graphql/sdk";
import { getError, getImageSrc, showToast } from "~lib";
import { useQuery } from "~hooks/useQuery";
import {
  HasMembershipToRenewDocument,
  MembershipsOverviewDocument,
} from "~graphql/typed-document-nodes";
import { useOrganization } from "~hooks";

type MembershipEvents = MembershipsQuery["memberships"]["edges"][number]["node"]["events"];
type MembershipZone = MembershipsQuery["memberships"]["edges"][number]["node"]["membershipZones"][number];
type MembershipType = MembershipZone["membershipTypes"][number];

const renderCosts = (membershipZones: MembershipZone[]) => {
  let cost: number[] = undefined;
  let costEl = null as ReactNode;

  if (membershipZones) {
    const membershipTypes = flatten(
      membershipZones?.filter((z) => z.isActive)?.map((z) => z.membershipTypes)
    )?.reduce(
      (
        acc: (MembershipType | MembershipType["sections"][number])[],
        curr: MembershipType
      ) => {
        acc = [
          ...acc,
          ...(curr?.sections?.length > 0
            ? curr.sections.filter((s) => s.price)
            : [curr]),
        ];

        return acc;
      },
      []
    );

    if (
      minBy(membershipTypes, "price") &&
      !Number.isNaN(minBy(membershipTypes, "price").price)
    ) {
      const membershipMin = minBy(membershipTypes, "price").price;
      const membershipMax = maxBy(membershipTypes, "price").price;

      cost = [membershipMin, membershipMax];
    }
  }

  if (cost && cost[0] === cost[1]) {
    costEl = <Price lineHeight="normal" price={cost[0]} />;
  } else if (cost) {
    costEl = (
      <>
        <Price lineHeight="normal" price={cost[0]} /> -{" "}
        <Price lineHeight="normal" price={cost[1]} />
      </>
    );
  }

  return (
    <LegacyText
      fontSize={3}
      fontWeight="regular"
      letterSpacing={"-0.17px" as any}
      mt={1}
      lineHeight="normal"
    >
      {costEl}
    </LegacyText>
  );
};

const getMembershipVenueNames = (events: MembershipEvents) => {
  const eventVenueList = events.map(
    (membershipEvent) => membershipEvent.venueAddress
  );
  return uniqBy(eventVenueList, "name")
    .map((venue) => `${venue.name}, ${venue?.address?.city}`)
    .join(", ");
};

const useGetPageData = () => {
  const { organization } = useOrganization();
  const {
    data: membershipsData,
    error: membershipsError,
    isLoading,
  } = useQuery(MembershipsOverviewDocument);

  const {
    pointItems,
    isPointsEnabled,
    error: pointsError,
    isLoading: pointsLoading,
  } = usePointsService({
    includeItems: true,
  });

  const { data: myMembershipsData } = useQuery(
    organization?.showMembershipRenewal && HasMembershipToRenewDocument
  );

  const myMemberships = myMembershipsData?.myMembershipTickets;

  const membershipsLoading = isLoading;
  const memberships = membershipsData?.memberships.edges?.map(
    ({ node }) => node
  );

  return {
    error: membershipsError || (isPointsEnabled && pointsError),
    isLoading: membershipsLoading || pointsLoading,
    pointItems,
    memberships,
    myMemberships,
    pointsLoading,
    membershipsLoading,
  };
};

const Memberships = () => {
  const router = useRouter();
  const { organization, t } = useOrganization();
  const { isLoggedIn } = useContext(UserContext);

  const {
    memberships,
    myMemberships,
    pointItems,
    isLoading,
    error,
  } = useGetPageData();

  if (error) {
    showToast(getError(error, "graphQL"), "error");
  }
  const hasMembershipToRenew = myMemberships?.some(
    (membership) =>
      !membership.hasRenewedMembership &&
      membership.membershipType?.membership.renewalMembership?.id
  );

  const showBanner =
    organization?.showMembershipRenewal && isLoggedIn && hasMembershipToRenew;

  const showNonLoggedInBanner =
    organization?.showMembershipRenewal && !isLoggedIn;

  return (
    <>
      <Seo
        title={`${t("membership_name_plural")} - ${organization?.name}`}
        description={undefined}
      />
      <Menu>
        <SubHeader>
          <LegacyText
            fontSize={7}
            lineHeight="medium"
            fontWeight="heavy"
            color="white"
            variant="capitalize"
          >
            {organization?.landingPage === LandingPage.SingleEvent
              ? "Events"
              : t("membership_name_plural")}
          </LegacyText>
        </SubHeader>
      </Menu>
      <Layout layoutType="main">
        <ContentContainer variant="content">
          <Flex flexDir="column" flex="1">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              maxWidth="1248px"
              mx={"auto" as any}
              pl={4}
              mb={4}
              d={{ _: "none !important", md: "block !important" }}
            >
              <LegacyText
                color="white"
                fontWeight="heavy"
                fontSize={9}
                lineHeight={"56px" as any}
              >
                {organization?.landingPage === LandingPage.SingleEvent
                  ? "Events"
                  : t("membership_name_plural")}
              </LegacyText>
            </Flex>
            <Container
              innerProps={{ minHeight: { _: 0, md: "320px" } }}
              mb={10}
            >
              {showBanner && <RenewalBanner mb={3} button="primary" />}
              {showNonLoggedInBanner && <NonLoggedInRenewalBanner mb={3} />}
              <Flex flexWrap="wrap" m={"-12px" as any} pb="6/4">
                <Status
                  noResult={!memberships?.length && !pointItems?.length}
                  noResultText={`${t(
                    "membership_name_plural"
                  )} not on sale yet.`}
                  error={error}
                  loading={isLoading}
                >
                  {!!pointItems?.length &&
                    pointItems.map(({ id, name, price, amount, thumbnail }) => (
                      <Box
                        p="6/4"
                        width={{ _: "100%", sm: "50%", md: "33.33%" }}
                        key={id}
                        onMouseOver={() => {
                          void router.prefetch("packages/" + id);
                        }}
                      >
                        <Card
                          minHeight="100%"
                          linkProps={{
                            to: `/packages/[id]`,
                            linkAs: `/packages/${id}`,
                          }}
                          image={getImageSrc(thumbnail)}
                        >
                          <LegacyText
                            as={"h1"}
                            fontSize={2}
                            lineHeight="medium"
                            fontWeight="heavy"
                            variant="uppercase"
                            letterSpacing={"-0.17px" as any}
                          >
                            {name}
                          </LegacyText>
                          <LegacyText>
                            {amount} {organization.point?.name} valued at{" "}
                            <Price lineHeight="normal" price={amount} />
                          </LegacyText>

                          <LegacyText
                            fontSize={3}
                            fontWeight="regular"
                            letterSpacing={"-0.17px" as any}
                            mt={1}
                            lineHeight="normal"
                          >
                            <Price lineHeight="normal" price={price} />
                          </LegacyText>
                        </Card>
                      </Box>
                    ))}
                  {!!memberships?.length &&
                    memberships.map(
                      ({
                        id,
                        thumbnail,
                        name,
                        membershipZones,
                        membershipKind,
                        events,
                      }) => (
                        <Box
                          p="6/4"
                          width={{ _: "100%", sm: "50%", md: "33.33%" }}
                          key={id}
                          onMouseOver={() => {
                            void router.prefetch("memberships/" + id);
                          }}
                        >
                          <Card
                            minHeight="100%"
                            linkProps={{
                              to: `/memberships/[membershipId]`,
                              linkAs: `/memberships/${id}`,
                            }}
                            image={getImageSrc(thumbnail)}
                          >
                            <LegacyText
                              as={"h1"}
                              fontSize={2}
                              lineHeight="medium"
                              fontWeight="heavy"
                              variant="uppercase"
                              letterSpacing={"-0.17px" as any}
                            >
                              {" "}
                              {`${name}${
                                membershipKind === MembershipKind.Digital
                                  ? ` (Digital ${t("membership_name")})`
                                  : ""
                              }`}
                            </LegacyText>
                            <LegacyText as={"h2"}>
                              {getMembershipVenueNames(events)}
                            </LegacyText>
                            {renderCosts(membershipZones)}
                          </Card>
                        </Box>
                      )
                    )}
                </Status>
              </Flex>
            </Container>
          </Flex>
          <Footer />
        </ContentContainer>
      </Layout>
      <BottomBar />
    </>
  );
};

export default Memberships;
